import { CircleAlert, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deleteGuide } from '@/data/guidesRepo';
import { useGuides } from '@/hooks/useGuides';
import { formatMonthYear } from '@/util/util_format';
import {
  formatGuideExperience,
  guideSpecialtyLabels,
  guideStatusLabels,
  type GuideStatusType,
  type GuideType,
} from '@/types/types_guide';

const statusPillClass: Record<GuideStatusType, string> = {
  active: 'tw:bg-green-100 tw:text-green-800',
  inactive: 'tw:bg-gray-100 tw:text-gray-800',
};

/** Admin list of tour leaders, styled like the other CMS list pages. */
export function PageGuideList() {
  const { guides, loading } = useGuides();
  const [toDelete, setToDelete] = useState<GuideType | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function confirmDelete() {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteGuide(toDelete.id);
      setToDelete(null);
      // The snapshot listener removes the row automatically.
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <header className='tw:mb-6 tw:flex tw:items-center tw:justify-between'>
        <div>
          <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Tour Leaders</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
            The roster shown on the public Tour Leaders page. {guides.length} tour leaders.
          </p>
        </div>
        <Link
          to='/admin/guides/new'
          className='tw:flex tw:items-center tw:gap-2 tw:bg-ed-gold tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
        >
          <Plus className='tw:size-4' /> Add Tour Leader
        </Link>
      </header>

      <div className='tw:overflow-x-auto tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <table className='tw:w-full'>
          <thead className='tw:bg-ed-cream/50'>
            <tr>
              {['Tour Leader', 'Specialties', 'Languages', 'Experience', 'Portal Calendar', 'Status', ''].map((h) => (
                <th
                  key={h}
                  className='tw:px-6 tw:py-3 tw:text-left tw:text-xs tw:font-medium tw:tracking-wider tw:text-ed-stone tw:uppercase'
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  Loading…
                </td>
              </tr>
            ) : guides.length === 0 ? (
              <tr>
                <td colSpan={7} className='tw:px-6 tw:py-10 tw:text-center tw:text-ed-stone'>
                  No tour leaders yet.
                </td>
              </tr>
            ) : (
              guides.map((guide) => (
                <tr key={guide.id} className='tw:border-t tw:border-ed-umber/10 tw:hover:bg-ed-cream/20'>
                  <td className='tw:px-6 tw:py-4'>
                    <div className='tw:flex tw:items-center tw:gap-3'>
                      {guide.photo ? (
                        <img
                          src={guide.photo}
                          alt=''
                          className='tw:size-10 tw:shrink-0 tw:rounded-full tw:object-cover'
                        />
                      ) : (
                        <div className='tw:size-10 tw:shrink-0 tw:rounded-full tw:bg-ed-cream' />
                      )}
                      <div>
                        <div className='tw:text-sm tw:font-medium tw:text-ed-black'>{guide.name}</div>
                        {guide.nameJp && <div className='tw:text-xs tw:text-ed-stone'>{guide.nameJp}</div>}
                      </div>
                    </div>
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    {guide.specialties.length === 0 ? (
                      <span className='tw:text-ed-stone'>—</span>
                    ) : (
                      <div className='tw:flex tw:flex-wrap tw:gap-1'>
                        {guide.specialties.map((s) => (
                          <span key={s} className='tw:rounded-full tw:bg-ed-cream tw:px-2 tw:py-0.5 tw:text-xs'>
                            {guideSpecialtyLabels[s]}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>{guide.languages.join(', ') || '—'}</td>
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    {formatGuideExperience(guide.guidingSince)}
                    {guide.guidingSince && (
                      <div className='tw:text-xs tw:text-ed-stone'>since {formatMonthYear(guide.guidingSince)}</div>
                    )}
                  </td>
                  {/* Worth a column of its own: a guide without one shows no
                      availability on the public page, and nothing else on this
                      screen would say why. */}
                  <td className='tw:px-6 tw:py-4 tw:text-sm'>
                    {guide.portalGuideId ? (
                      <code className='tw:text-xs tw:text-ed-stone'>{guide.portalGuideId}</code>
                    ) : (
                      <span
                        title='No portal calendar linked — the public page cannot show this tour leader’s availability'
                        className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-ed-red'
                      >
                        <CircleAlert className='tw:size-3.5' /> Not linked
                      </span>
                    )}
                  </td>
                  <td className='tw:px-6 tw:py-4'>
                    <span className={`tw:rounded-full tw:px-2 tw:py-1 tw:text-xs ${statusPillClass[guide.status]}`}>
                      {guideStatusLabels[guide.status]}
                    </span>
                  </td>
                  <td className='tw:px-6 tw:py-4'>
                    <div className='tw:flex tw:justify-end tw:gap-2'>
                      <Link
                        to={`/admin/guides/${guide.id}/edit`}
                        title='Edit'
                        className='tw:border tw:border-ed-umber/30 tw:p-2 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-gold tw:hover:text-ed-gold'
                      >
                        <Pencil className='tw:size-4' />
                      </Link>
                      <button
                        type='button'
                        title='Delete'
                        onClick={() => setToDelete(guide)}
                        className='tw:border tw:border-ed-umber/30 tw:p-2 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-red tw:hover:text-ed-red'
                      >
                        <Trash2 className='tw:size-4' />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!toDelete} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete tour leader?</DialogTitle>
            <DialogDescription>
              This will remove &ldquo;{toDelete?.name}&rdquo; from the roster. This cannot be undone — to take a
              tour leader off the public page without losing their profile, set their status to Inactive
              instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' disabled={deleting} onClick={() => setToDelete(null)}>
              Cancel
            </Button>
            <Button variant='destructive' loading={deleting} onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
